import {
  Activity,
  Award,
  BarChart3,
  Calendar,
  Gift,
  QrCode,
  Star,
  Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { statsService } from "../services/api";

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalParents: 0,
    totalTampons: 0,
    totalActivites: 0,
    totalRecompenses: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);

      // Données de démonstration
      const demoStats = {
        totalParents: 156,
        totalTampons: 1247,
        totalActivites: 23,
        totalRecompenses: 89,
      };

      setStats(demoStats);

      // Essayer de récupérer les vraies données
      try {
        const [
          parentsResponse,
          tamponsResponse,
          activitesResponse,
          recompensesResponse,
        ] = await Promise.all([
          statsService.getParentStats(),
          statsService.getTamponStats(),
          statsService.getActiviteStats(),
          statsService.getRecompenseStats(),
        ]);

        setStats({
          totalParents: parentsResponse.data.total || demoStats.totalParents,
          totalTampons: tamponsResponse.data.total || demoStats.totalTampons,
          totalActivites:
            activitesResponse.data.total || demoStats.totalActivites,
          totalRecompenses:
            recompensesResponse.data.total || demoStats.totalRecompenses,
        });
      } catch (apiError) {
        console.log(
          "API non disponible, utilisation des données de démonstration"
        );
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Erreur lors du chargement des statistiques");
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      name: "Scanner QR Code",
      description: "Valider la participation",
      href: "/app/qr-scanner",
      icon: QrCode,
      color: "bg-blue-600 hover:bg-blue-700",
    },
    {
      name: "Gérer les Activités",
      description: "Créer et modifier",
      href: "/app/activites",
      icon: Calendar,
      color: "bg-green-600 hover:bg-green-700",
    },
    {
      name: "Gérer les Récompenses",
      description: "Créer et modifier",
      href: "/app/recompenses",
      icon: Gift,
      color: "bg-purple-600 hover:bg-purple-700",
    },
    {
      name: "Voir les Statistiques",
      description: "Analyser les données",
      href: "/app/statistiques",
      icon: BarChart3,
      color: "bg-orange-600 hover:bg-orange-700",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Tableau de bord Administrateur
            </h1>
            <p className="text-gray-600 mt-1">
              Bienvenue, {user.nom} ! Gérez l'engagement parental de votre
              école.
            </p>
          </div>
          <div className="flex items-center space-x-2 px-3 py-2 bg-blue-100 rounded-lg">
            <Star className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-800">
              {user.role === "superadmin" ? "Super Admin" : "Administrateur"}
            </span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Parents */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Parents Inscrits
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalParents}
              </p>
            </div>
          </div>
        </div>

        {/* Total Tampons */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Award className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Tampons Validés
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalTampons}
              </p>
            </div>
          </div>
        </div>

        {/* Total Activités */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Calendar className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Activités Créées
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalActivites}
              </p>
            </div>
          </div>
        </div>

        {/* Total Récompenses */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Gift className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Récompenses Attribuées
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalRecompenses}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions - 2 colonnes */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">
              Actions Rapides
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Link
                    key={action.name}
                    to={action.href}
                    className="group p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex items-center space-x-4">
                      <div
                        className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center transition-colors duration-200`}
                      >
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                          {action.name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {action.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recent Activity - 1 colonne */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">
              Activité Récente
            </h2>
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Award className="h-4 w-4 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 text-sm">
                    Nouveau tampon validé
                  </p>
                  <p className="text-xs text-gray-600">
                    Marie Mbarga - Réunion parents
                  </p>
                </div>
                <span className="text-xs text-gray-500">2 min</span>
              </div>

              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="h-4 w-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 text-sm">
                    Nouveau parent inscrit
                  </p>
                  <p className="text-xs text-gray-600">
                    Jean Dupont - Parent de 3ème B
                  </p>
                </div>
                <span className="text-xs text-gray-500">15 min</span>
              </div>

              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Gift className="h-4 w-4 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 text-sm">
                    Récompense attribuée
                  </p>
                  <p className="text-xs text-gray-600">
                    Certificat Bronze - Sophie Mbarga
                  </p>
                </div>
                <span className="text-xs text-gray-500">1h</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Overview */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            Engagement Parental
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Taux de participation
              </span>
              <span className="text-sm font-semibold text-gray-900">78%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full"
                style={{ width: "78%" }}
              ></div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Parents actifs ce mois
              </span>
              <span className="text-sm font-semibold text-gray-900">142</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full"
                style={{ width: "91%" }}
              ></div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            Top Activités
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Calendar className="h-4 w-4 text-blue-600" />
                </div>
                <span className="text-sm font-medium text-gray-900">
                  Réunion parents
                </span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                45 participants
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Activity className="h-4 w-4 text-green-600" />
                </div>
                <span className="text-sm font-medium text-gray-900">
                  Sortie éducative
                </span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                32 participants
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Award className="h-4 w-4 text-purple-600" />
                </div>
                <span className="text-sm font-medium text-gray-900">
                  Atelier cuisine
                </span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                28 participants
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
